import React, { useCallback, useState } from 'react';
import { BarChart2, Save, RefreshCw, Download, Calculator, Percent, ArrowRightLeft } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import CurrentLoanSection from './CurrentLoanSection';
import NewLoanSection from './NewLoanSection';
import FeesSection from './FeesSection';
import OptimizationSection from './OptimizationSection';
import EarlyRepaymentSection from './EarlyRepaymentSection';

// Импорт утилитарных функций
import { isPositiveNumber, isInteger } from '../../../../../utils/validation';
import { calculateAnnuityPayment } from '../../../../../utils/calculations/finance';

/**
 * Компонент основной формы расчета рефинансирования
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  comparisonData,
  setComparisonData,
  schedule,
  setSchedule,
  earlyRepaymentPeriods,
  banks,
  saveComparison,
  selectedBank,
  setSelectedBank
}) => {
  
  // Состояние для имени сохраняемого сравнения
  const [saveComparisonName, setSaveComparisonName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Автозаполнение ежемесячного платежа при изменении других полей текущего кредита
      if (['currentLoanAmount', 'currentInterestRate', 'currentRemainingTerm', 'currentPaymentType'].includes(name) && 
          !prev.currentMonthlyPayment) {
        const amount = parseFloatOrDefault(name === 'currentLoanAmount' ? value : prev.currentLoanAmount, 0);
        const rate = parseFloatOrDefault(name === 'currentInterestRate' ? value : prev.currentInterestRate, 0);
        const term = parseFloatOrDefault(name === 'currentRemainingTerm' ? value : prev.currentRemainingTerm, 0);
        const termMonths = Math.round(term * 12);
        
        if (amount > 0 && rate > 0 && termMonths > 0) {
          const paymentType = name === 'currentPaymentType' ? value : prev.currentPaymentType;
          
          if (paymentType === 'annuity') {
            const monthlyPayment = calculateAnnuityPayment(amount, termMonths, rate);
            newState.currentMonthlyPayment = monthlyPayment.toFixed(2);
          }
          // Для дифференцированных платежей мы можем показать только первый платеж
          // Или оставить поле пустым, чтобы пользователь заполнил его сам
        }
      }

      // Сбрасываем связанные поля если функция отключена
      if (name === 'includeEarlyRepayment' && !checked) {
        newState.earlyRepaymentAmount = '50000';
        newState.earlyRepaymentPeriod = 'monthly';
        newState.earlyRepaymentStartMonth = '6';
      }
      
      if (name === 'includeFees' && !checked) {
        newState.initialFee = '0';
        newState.earlyRepaymentFee = '0';
        newState.insuranceCost = '0';
        newState.valuationCost = '0';
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, setErrors, setFormData]);

  // Функция для автоматического расчета текущего ежемесячного платежа
  const calculateCurrentPayment = useCallback(() => {
    const amount = parseFloatOrDefault(formData.currentLoanAmount, 0);
    const rate = parseFloatOrDefault(formData.currentInterestRate, 0);
    const term = parseFloatOrDefault(formData.currentRemainingTerm, 0);
    const termMonths = Math.round(term * 12);
    
    if (amount > 0 && rate > 0 && termMonths > 0) {
      if (formData.currentPaymentType === 'annuity') {
        const monthlyPayment = calculateAnnuityPayment(amount, termMonths, rate);
        setFormData(prev => ({
          ...prev,
          currentMonthlyPayment: monthlyPayment.toFixed(2)
        }));
      }
    }
  }, [formData.currentLoanAmount, formData.currentInterestRate, formData.currentRemainingTerm, formData.currentPaymentType, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      // Текущий кредит
      currentLoanAmount, currentInterestRate, currentRemainingTerm, currentMonthlyPayment,
      // Новый кредит
      newInterestRate, newLoanTerm, additionalAmount,
      // Досрочное погашение
      includeEarlyRepayment, earlyRepaymentAmount, earlyRepaymentStartMonth, earlyRepaymentPeriod,
      // Комиссии и сборы
      includeFees, initialFee, earlyRepaymentFee, insuranceCost, valuationCost
    } = formData;

    // Парсинг числовых значений текущего кредита
    const numCurrentLoanAmount = parseFloatOrDefault(currentLoanAmount, -1);
    const numCurrentInterestRate = parseFloatOrDefault(currentInterestRate, -1);
    const numCurrentRemainingTermYears = parseFloatOrDefault(currentRemainingTerm, -1);
    const numCurrentRemainingTermMonths = Math.round(numCurrentRemainingTermYears * 12);
    const numCurrentMonthlyPayment = parseFloatOrDefault(currentMonthlyPayment, -1);

    // Валидация полей текущего кредита
    if (numCurrentLoanAmount <= 0) newErrors.currentLoanAmount = 'Сумма должна быть положительной.';
    if (!newErrors.currentLoanAmount && numCurrentLoanAmount > 50000000) newErrors.currentLoanAmount = 'Максимальная сумма 50 млн ₽.';
    
    if (numCurrentInterestRate <= 0) newErrors.currentInterestRate = 'Ставка должна быть положительной.';
    if (!newErrors.currentInterestRate && numCurrentInterestRate > 100) newErrors.currentInterestRate = 'Ставка не может быть более 100%.';
    
    if (numCurrentRemainingTermYears <= 0) newErrors.currentRemainingTerm = 'Срок должен быть положительным.';
    if (!newErrors.currentRemainingTerm && numCurrentRemainingTermYears > 30) newErrors.currentRemainingTerm = 'Максимальный срок 30 лет.';
    if (!newErrors.currentRemainingTerm && numCurrentRemainingTermMonths <= 0) newErrors.currentRemainingTerm = 'Срок должен быть не менее 1 месяца.';
    
    if (currentMonthlyPayment && numCurrentMonthlyPayment <= 0) newErrors.currentMonthlyPayment = 'Платеж должен быть положительным.';

    // Парсинг числовых значений нового кредита
    const numNewInterestRate = parseFloatOrDefault(newInterestRate, -1);
    const numNewLoanTermYears = parseFloatOrDefault(newLoanTerm, -1);
    const numNewLoanTermMonths = Math.round(numNewLoanTermYears * 12);
    const numAdditionalAmount = parseFloatOrDefault(additionalAmount, 0);

    // Валидация полей нового кредита
    if (numNewInterestRate <= 0) newErrors.newInterestRate = 'Ставка должна быть положительной.';
    if (!newErrors.newInterestRate && numNewInterestRate > 100) newErrors.newInterestRate = 'Ставка не может быть более 100%.';
    
    if (numNewLoanTermYears <= 0) newErrors.newLoanTerm = 'Срок должен быть положительным.';
    if (!newErrors.newLoanTerm && numNewLoanTermYears > 30) newErrors.newLoanTerm = 'Максимальный срок 30 лет.';
    if (!newErrors.newLoanTerm && numNewLoanTermMonths <= 0) newErrors.newLoanTerm = 'Срок должен быть не менее 1 месяца.';
    
    if (numAdditionalAmount < 0) newErrors.additionalAmount = 'Сумма не может быть отрицательной.';

    // Проверка даты начала нового кредита
    if (!formData.startDate) newErrors.startDate = 'Укажите дату начала кредита.';
    try {
      const dateObject = new Date(formData.startDate);
      if (isNaN(dateObject.getTime())) {
        newErrors.startDate = 'Некорректная дата.';
      }
    } catch (e) {
      newErrors.startDate = 'Некорректная дата.';
    }

    // Валидация досрочного погашения
    if (includeEarlyRepayment) {
      const numEarlyRepaymentAmount = parseFloatOrDefault(earlyRepaymentAmount, -1);
      const numEarlyRepaymentStartMonth = parseIntOrDefault(earlyRepaymentStartMonth, -1);
      
      if (numEarlyRepaymentAmount <= 0) newErrors.earlyRepaymentAmount = 'Сумма должна быть положительной.';
      if (!newErrors.earlyRepaymentAmount && numEarlyRepaymentAmount >= numCurrentLoanAmount) {
        newErrors.earlyRepaymentAmount = 'Сумма досрочного погашения не может быть больше или равна остатку кредита.';
      }
      
      if (!isInteger(earlyRepaymentStartMonth) || numEarlyRepaymentStartMonth <= 0) {
        newErrors.earlyRepaymentStartMonth = 'Введите целый положительный номер месяца.';
      }
      
      if (!newErrors.earlyRepaymentStartMonth && numEarlyRepaymentStartMonth > numNewLoanTermMonths) {
        newErrors.earlyRepaymentStartMonth = `Начало (${numEarlyRepaymentStartMonth}) не может быть позже срока (${numNewLoanTermMonths} мес.).`;
      }
      
      if (!earlyRepaymentPeriod) newErrors.earlyRepaymentPeriod = "Выберите периодичность.";
    }

    // Валидация комиссий
    if (includeFees) {
      const numInitialFee = parseFloatOrDefault(initialFee, -1);
      const numEarlyRepaymentFee = parseFloatOrDefault(earlyRepaymentFee, -1);
      const numInsuranceCost = parseFloatOrDefault(insuranceCost, -1);
      const numValuationCost = parseFloatOrDefault(valuationCost, -1);
      
      if (numInitialFee < 0) newErrors.initialFee = 'Значение не может быть отрицательным.';
      if (numEarlyRepaymentFee < 0) newErrors.earlyRepaymentFee = 'Значение не может быть отрицательным.';
      if (numInsuranceCost < 0) newErrors.insuranceCost = 'Значение не может быть отрицательным.';
      if (numValuationCost < 0) newErrors.valuationCost = 'Значение не может быть отрицательным.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors, earlyRepaymentPeriods]);

  // Расчет платежа по аннуитетному кредиту
  // Функция расчета рефинансирования
  const calculateRefinancing = useCallback(() => {
    if (!validateForm()) {
      // Прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);
    setComparisonData(null);
    setSchedule([]);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // Подготовка данных
        const currentLoanAmount = parseFloatOrDefault(formData.currentLoanAmount);
        const currentInterestRate = parseFloatOrDefault(formData.currentInterestRate);
        const currentRemainingTermYears = parseFloatOrDefault(formData.currentRemainingTerm);
        const currentRemainingTermMonths = Math.round(currentRemainingTermYears * 12);
        const currentPaymentType = formData.currentPaymentType;
        const currentMonthlyPayment = parseFloatOrDefault(formData.currentMonthlyPayment) || 
          (currentPaymentType === 'annuity' 
            ? calculateAnnuityPaymentLocal(currentLoanAmount, currentRemainingTermMonths, currentInterestRate) 
            : calculateDifferentiatedPayment(currentLoanAmount, currentRemainingTermMonths, currentInterestRate, 1));
        
        const newLoanAmount = currentLoanAmount + parseFloatOrDefault(formData.additionalAmount, 0);
        const newInterestRate = parseFloatOrDefault(formData.newInterestRate);
        const newLoanTermYears = parseFloatOrDefault(formData.newLoanTerm);
        const newLoanTermMonths = Math.round(newLoanTermYears * 12);
        const newPaymentType = formData.newPaymentType;
        
        // Расчет комиссий
        const initialFee = formData.includeFees ? parseFloatOrDefault(formData.initialFee, 0) : 0;
        const earlyRepaymentFee = formData.includeFees ? parseFloatOrDefault(formData.earlyRepaymentFee, 0) : 0;
        const insuranceCost = formData.includeFees ? parseFloatOrDefault(formData.insuranceCost, 0) : 0;
        const valuationCost = formData.includeFees ? parseFloatOrDefault(formData.valuationCost, 0) : 0;
        const totalFees = initialFee + earlyRepaymentFee + insuranceCost + valuationCost;
        
        // Расчет досрочного погашения
        const earlyPaymentData = formData.includeEarlyRepayment ? {
          enabled: true,
          amount: parseFloatOrDefault(formData.earlyRepaymentAmount, 0),
          period: formData.earlyRepaymentPeriod,
          startMonth: parseIntOrDefault(formData.earlyRepaymentStartMonth, 6),
          type: formData.optimizationGoal === 'payment' ? 'payment' : 'term'
        } : null;
        
        // Расчет текущего кредита
        const currentLoanResult = calculateSchedule(
          currentLoanAmount,
          currentRemainingTermMonths,
          currentInterestRate,
          currentPaymentType
        );
        
        // Расчет нового кредита (рефинансирования)
        const newLoanResult = calculateSchedule(
          newLoanAmount,
          newLoanTermMonths,
          newInterestRate,
          newPaymentType,
          earlyPaymentData
        );
        
        // Расчет экономии
        const currentTotalPayments = currentLoanResult.schedule.reduce((sum, item) => sum + item.payment, 0);
        const newTotalPayments = newLoanResult.schedule.reduce((sum, item) => sum + item.totalPayment, 0);
        const totalSavings = currentTotalPayments - newTotalPayments - totalFees;
        
        // Расчет средних платежей
        const currentAveragePayment = currentTotalPayments / currentLoanResult.schedule.length;
        const newAveragePayment = newTotalPayments / newLoanResult.schedule.length;
        const paymentDifference = currentAveragePayment - newAveragePayment;
        
        // Расчет срока возврата инвестиций в рефинансирование
        const monthlyBenefit = paymentDifference > 0 ? paymentDifference : 0;
        const roiMonths = monthlyBenefit > 0 ? Math.ceil(totalFees / monthlyBenefit) : Infinity;
        
        // Определение целесообразности рефинансирования
        const isRefinancingEffective = totalSavings > 0 && roiMonths < newLoanResult.effectiveTermMonths;
        
        // Формирование результатов
        const resultData = {
          // Показатели текущего кредита
          currentLoanAmount,
          currentInterestRate,
          currentMonthlyPayment,
          currentRemainingTermMonths,
          currentTotalPayments,
          currentTotalInterest: currentLoanResult.totalInterestPaid,
          
          // Показатели нового кредита
          newLoanAmount,
          newInterestRate,
          newMonthlyPayment: newLoanResult.schedule.length > 0 ? newLoanResult.schedule[0].payment : 0,
          newLoanTermMonths: newLoanResult.effectiveTermMonths,
          newTotalPayments,
          newTotalInterest: newLoanResult.totalInterestPaid,
          
          // Экономия и выгода
          totalSavings,
          paymentDifference,
          effectiveInterestRate: newInterestRate,
          
          // Комиссии и сборы
          totalFees,
          initialFee,
          earlyRepaymentFee,
          insuranceCost,
          valuationCost,
          
          // Показатели эффективности
          isRefinancingEffective,
          roiMonths: isFinite(roiMonths) ? roiMonths : null,
          paymentReduction: paymentDifference > 0,
          termReduction: newLoanResult.effectiveTermMonths < currentRemainingTermMonths,
          
          // Дополнительные данные
          additionalAmount: parseFloatOrDefault(formData.additionalAmount, 0),
          startDate: new Date(formData.startDate),
          bank: selectedBank
        };
        
        // Формирование данных для графика
        const comparisonChartData = {
          labels: ['Текущий кредит', 'Новый кредит'],
          datasets: [
            {
              label: 'Основной долг',
              data: [currentLoanAmount, newLoanAmount],
              backgroundColor: ['rgba(99, 102, 241, 0.8)', 'rgba(99, 102, 241, 0.8)']
            },
            {
              label: 'Проценты',
              data: [currentLoanResult.totalInterestPaid, newLoanResult.totalInterestPaid],
              backgroundColor: ['rgba(244, 63, 94, 0.8)', 'rgba(244, 63, 94, 0.8)']
            },
            {
              label: 'Комиссии и сборы',
              data: [0, totalFees],
              backgroundColor: ['rgba(234, 179, 8, 0.8)', 'rgba(234, 179, 8, 0.8)']
            }
          ],
          monthlySavings: paymentDifference,
          totalSavings,
          effectiveness: resultData.isRefinancingEffective ? 
            (resultData.roiMonths ? `Окупится через ${resultData.roiMonths} мес.` : 'Выгодно') : 
            'Невыгодно'
        };
        
        // Сохраняем результаты и график
        setResults(resultData);
        setComparisonData(comparisonChartData);
        setSchedule(newLoanResult.schedule);
        
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setComparisonData, setSchedule, setIsCalculating, selectedBank]);

  // Расчет аннуитетного платежа
  const calculateAnnuityPaymentLocal = (principal, termMonths, annualRate) => {
    if (principal <= 0 || termMonths <= 0) return 0;
    const monthlyRate = annualRate / 12 / 100;

    if (monthlyRate === 0) {
      return principal / termMonths;
    }

    const factor = Math.pow(1 + monthlyRate, termMonths);
    if (!isFinite(factor) || factor === 1) {
      return principal / termMonths;
    }
    return principal * monthlyRate * factor / (factor - 1);
  };

  // Расчет дифференцированного платежа
  const calculateDifferentiatedPayment = (principal, termMonths, annualRate, month) => {
    const monthlyRate = annualRate / 12 / 100;
    const principalPayment = principal / termMonths;
    const remainingPrincipal = principal - principalPayment * (month - 1);
    const interestPayment = remainingPrincipal * monthlyRate;
    
    return principalPayment + interestPayment;
  };

  // Сброс формы
  const resetForm = () => {
    setFormData({
      // Данные текущего кредита
      currentLoanAmount: '1000000',         // Остаток по текущему кредиту
      currentInterestRate: '14.5',          // Текущая процентная ставка
      currentRemainingTerm: '3',            // Оставшийся срок (в годах)
      currentMonthlyPayment: '',            // Текущий ежемесячный платеж (опционально)
      currentPaymentType: 'annuity',        // Тип платежа (аннуитетный/дифференцированный)
      alreadyPaid: '500000',                // Уже выплаченная сумма
      
      // Данные нового кредита (рефинансирование)
      newInterestRate: '9.5',               // Новая процентная ставка
      newLoanTerm: '3',                     // Новый срок кредита (в годах)
      additionalAmount: '0',                // Дополнительная сумма к рефинансированию
      newPaymentType: 'annuity',            // Тип платежа нового кредита
      startDate: new Date().toISOString().split('T')[0], // Дата начала нового кредита
      
      // Дополнительные параметры
      includeFees: true,                    // Учитывать комиссии
      initialFee: '10000',                  // Единоразовая комиссия
      earlyRepaymentFee: '0',               // Комиссия за досрочное погашение текущего кредита
      insuranceCost: '15000',               // Стоимость страховки
      valuationCost: '5000',                // Стоимость оценки (для ипотеки)
      
      // Предпочтения оптимизации
      optimizationGoal: 'payment',          // Цель: снижение платежа ('payment') или срока ('term')
      includeEarlyRepayment: false,         // Учитывать досрочное погашение
      earlyRepaymentAmount: '50000',        // Сумма досрочного погашения
      earlyRepaymentPeriod: 'monthly',      // Периодичность досрочного погашения
      earlyRepaymentStartMonth: '6',        // Месяц начала досрочных платежей
    });
    setResults(null);
    setComparisonData(null);
    setSchedule([]);
    setErrors({});
    setSaveComparisonName('');
    setShowSaveDialog(false);
    setSelectedBank(null);
  };

  // Экспорт графика платежей в CSV
  const exportToCSV = () => {
    if (!schedule || schedule.length === 0 || !results) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Определяем заголовки на основе расчетов
    const headers = ['Месяц', 'Дата', 'Платеж (Осн. долг + %)', 'Основной долг', 'Проценты'];
    if (formData.includeEarlyRepayment) headers.push('Досрочное погашение');
    headers.push('Общий платеж за месяц', 'Остаток долга');

    const formatCsvNumber = (num) => num.toFixed(2).replace('.', ',');

    const rows = schedule.map(item => {
      // Вычисляем дату платежа
      const paymentDate = new Date(results.startDate);
      paymentDate.setMonth(paymentDate.getMonth() + item.month - 1);
      
      const rowData = [
        item.month,
        paymentDate.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }),
        formatCsvNumber(item.payment),
        formatCsvNumber(item.principalPayment),
        formatCsvNumber(item.interestPayment),
      ];
      
      if (formData.includeEarlyRepayment) {
        rowData.push(formatCsvNumber(item.earlyRepayment || 0));
      }
      
      rowData.push(
        formatCsvNumber(item.totalPayment),
        formatCsvNumber(item.remainingBalance)
      );
      
      return rowData.join(';');
    });

    // Добавим итоговую строку
    const totalRow = [
      '', 'ИТОГО:', 
      formatCsvNumber(schedule.reduce((sum, item) => sum + item.payment, 0)),
      formatCsvNumber(schedule.reduce((sum, item) => sum + item.principalPayment, 0)),
      formatCsvNumber(schedule.reduce((sum, item) => sum + item.interestPayment, 0))
    ];
    
    if (formData.includeEarlyRepayment) {
      totalRow.push(formatCsvNumber(schedule.reduce((sum, item) => sum + (item.earlyRepayment || 0), 0)));
    }
    
    totalRow.push(
      formatCsvNumber(schedule.reduce((sum, item) => sum + item.totalPayment, 0)), 
      ''
    );
    
    // Добавим основную информацию в начало файла
    const infoRows = [
      ['Текущий кредит:', formatCsvNumber(results.currentLoanAmount)],
      ['Ставка текущего кредита:', `${results.currentInterestRate}%`],
      ['Оставшийся срок текущего кредита:', `${results.currentRemainingTermMonths} мес.`],
      [''],
      ['Новый кредит:', formatCsvNumber(results.newLoanAmount)],
      ['Ставка нового кредита:', `${results.newInterestRate}%`],
      ['Срок нового кредита:', `${results.newLoanTermMonths} мес.`],
      ['Экономия на платеже:', formatCsvNumber(results.paymentDifference)],
      ['Общая экономия:', formatCsvNumber(results.totalSavings)],
      ['Банк:', results.bank ? results.bank.name : 'Не указан'],
      [''],  // Пустая строка для разделения
    ].map(row => row.join(';'));

    // UTF-8 BOM для правильной кодировки в Excel
    const csvContent = '\uFEFF' + [...infoRows, headers.join(';'), ...rows, totalRow.join(';')].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'график_рефинансирования.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения сравнения
  const handleSaveComparison = () => {
    if (showSaveDialog) {
      const savedResult = saveComparison(saveComparisonName || null);
      if (savedResult) {
        alert(`Рефинансирование "${savedResult.name}" успешно сохранено!`);
      }
      setShowSaveDialog(false);
      setSaveComparisonName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Функция расчета графика платежей
  const calculateSchedule = (loanAmount, termMonths, annualRate, paymentType, earlyPaymentData = null) => {
    // Инициализируем переменные для расчета
    let currentBalance = loanAmount;
    const schedule = [];
    let totalInterestPaid = 0;
    let totalEarlyRepaymentPaid = 0;
    
    // Расчет начального аннуитетного платежа (если применимо)
    let fixedAnnuityPayment = (paymentType === 'annuity')
      ? calculateAnnuityPaymentLocal(loanAmount, termMonths, annualRate)
      : 0;
    
    // Определяем частоту досрочного погашения
    let earlyRepaymentFrequencyMonths = 0;
    let earlyRepaymentAmount = 0;
    let earlyRepaymentStartMonth = 1;
    
    if (earlyPaymentData && earlyPaymentData.enabled) {
      earlyRepaymentAmount = earlyPaymentData.amount;
      earlyRepaymentStartMonth = earlyPaymentData.startMonth;
      
      switch (earlyPaymentData.period) {
        case 'monthly': earlyRepaymentFrequencyMonths = 1; break;
        case 'quarterly': earlyRepaymentFrequencyMonths = 3; break;
        case 'semi_annually': earlyRepaymentFrequencyMonths = 6; break;
        case 'annually': earlyRepaymentFrequencyMonths = 12; break;
        case 'once': earlyRepaymentFrequencyMonths = -1; break;
        default: earlyRepaymentFrequencyMonths = 0;
      }
    }
    
    // Цикл расчета графика платежей
    for (let month = 1; month <= termMonths && currentBalance > 0.01; month++) {
      // Расчет ежемесячных процентов
      const monthlyRate = annualRate / 12 / 100;
      let interestPayment = currentBalance * monthlyRate;
      
      // Расчет платежа по основному долгу
      let principalPayment = 0;
      let monthlyPayment = 0;
      
      if (paymentType === 'annuity') {
        monthlyPayment = fixedAnnuityPayment;
        principalPayment = monthlyPayment - interestPayment;
        
        if (currentBalance + interestPayment <= monthlyPayment + 0.01) {
          principalPayment = currentBalance;
          monthlyPayment = currentBalance + interestPayment;
        }
      } else { // Дифференцированный
        principalPayment = loanAmount / termMonths;
        if (currentBalance < principalPayment) {
          principalPayment = currentBalance;
        }
        monthlyPayment = principalPayment + interestPayment;
      }
      
      // Расчет досрочного погашения
      let earlyRepayment = 0;
      if (earlyPaymentData && earlyPaymentData.enabled && currentBalance > 0 && month >= earlyRepaymentStartMonth) {
        const isRepaymentMonth =
          (earlyRepaymentFrequencyMonths === -1 && month === earlyRepaymentStartMonth) ||
          (earlyRepaymentFrequencyMonths > 0 && (month - earlyRepaymentStartMonth) % earlyRepaymentFrequencyMonths === 0);
        
        if (isRepaymentMonth) {
          earlyRepayment = Math.min(earlyRepaymentAmount, currentBalance - principalPayment);
          earlyRepayment = Math.max(0, earlyRepayment);
        }
      }
      
      // Обновляем общие суммы
      totalInterestPaid += interestPayment;
      totalEarlyRepaymentPaid += earlyRepayment;
      
      // Обновляем остаток кредита
      const startingBalance = currentBalance;
      currentBalance -= (principalPayment + earlyRepayment);
      currentBalance = Math.max(0, currentBalance);
      
      // Пересчитываем аннуитетный платеж после досрочного погашения
      if (earlyRepayment > 0 && paymentType === 'annuity' && currentBalance > 0.01 && earlyPaymentData.type === 'payment') {
        const remainingMonths = termMonths - month;
        if (remainingMonths > 0) {
          fixedAnnuityPayment = calculateAnnuityPaymentLocal(currentBalance, remainingMonths, annualRate);
        }
      }
      
      // Добавляем запись в график платежей
      schedule.push({
        month,
        payment: monthlyPayment,
        principalPayment,
        interestPayment,
        startingBalance,
        remainingBalance: currentBalance,
        earlyRepayment,
        totalPayment: monthlyPayment + earlyRepayment
      });
      
      // Если долг погашен, выходим из цикла
      if (currentBalance <= 0.01) break;
    }
    
    return {
      schedule,
      totalInterestPaid,
      totalEarlyRepaymentPaid,
      effectiveTermMonths: schedule.length
    };
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateRefinancing();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <ArrowRightLeft className="w-6 h-6 text-indigo-500" />
          Параметры рефинансирования кредита
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Секция текущего кредита */}
        <CurrentLoanSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors}
          calculateCurrentPayment={calculateCurrentPayment}
        />

        {/* Секция нового кредита */}
        <NewLoanSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors}
          selectedBank={selectedBank}
        />

        {/* Секция оптимизации */}
        <OptimizationSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors}
        />

        {/* Секция комиссий */}
        <FeesSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors}
        />

        {/* Секция досрочного погашения */}
        <EarlyRepaymentSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors}
          earlyRepaymentPeriods={earlyRepaymentPeriods}
        />

        {/* Диалог сохранения сравнения */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveComparisonName}
                onChange={(e) => setSaveComparisonName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveComparison}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<Calculator className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать рефинансирование'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {schedule.length > 0 && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать график (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveComparison}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить расчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;